<template>
  <div class="table-container" v-if="canVerb(resource, 'index')">
    <actions-toolbar>
      <template #filters>
        <div class="status-filter">
          <div class="pill-wrapper">
            <span
              class="pill"
              v-for="(value, name, index) in activeFilters"
              :key="index"
              data-color="white"
            >
              <span v-b-toggle.sidebar-1 :data-filter="name"
                ><strong>{{ sidebarFilterName(name, context) }}</strong
                >:&nbsp;{{ sidebarFilterValue(name, value) }}</span
              >
              <span tabindex="0">
                <span
                  @click="resetSingleSidebarFilter(name)"
                  title="Rimuovi filtro"
                  >×</span
                >
              </span>
            </span>
            <template v-if="countActiveFilters">
              <span
                class="pill"
                data-color="alert"
                @click="resetAllFilters()"
                title="Rimuovi i filtri e apri il pannello per una nuova ricerca"
              >
                <span
                  ><strong>Reset ({{ countActiveFilters }})</strong></span
                >
                <span><span>×</span></span>
              </span>
            </template>
          </div>
        </div>
      </template>
      <b-button-group
        v-b-toggle.sidebar-1
        :class="`filter-button-group btn--smaller btn--transparent align-items-center gap-3${
          countActiveFilters ? ' active' : ''
        }`"
        :title="getDefaultFilterMessage()"
      >
        <base-icon name="filters" width="23" height="23" />
        <span style="font-size: 14px; font-weight: 500">{{
          computedFilterTitle
        }}</span>
      </b-button-group>
      <b-button
        v-if="!isLoadingCount && canVerb(resource, 'store')"
        :id="`popover-create`"
        class="btn-create btn--smaller"
        type="button"
        variant="primary"
        size="sm"
        @click="onAdd"
        title="Crea"
      >
        <base-icon name="circled_plus" width="22" height="22"/>
        Crea
      </b-button
      >
    </actions-toolbar>

    <b-sidebar
      id="sidebar-1"
      :title="computedFilterTitle"
      :backdrop="true"
      width="50dvw"
      shadow
      right
      :visible="open_filter"
      :data-open="open_filter"
      @change="onSidebarFilterChange"
    >
      <template #header="{ hide }">
        <div class="inner-header">
          <div class="inner-header--create">
            <strong class="mr-auto"> Crea un nuovo utente </strong>
            <b-button
              class="btn btn--create btn--smaller"
              type="button"
              variant="primary"
              size="sm"
              @click="onAdd"
              title="Crea un nuovo utente"
              :disabled="isLoadingCount || !canVerb(resource, 'store')"
            >
              <base-icon name="circled_plus" width="16" height="16" />
              <span>Crea</span>
            </b-button>
          </div>
          <div class="inner-header--info">
            <strong class="mr-auto">
              {{ computedFilterTitle }}
            </strong>
            <button class="close text-dark" @click="hide">
              <b-icon icon="x"></b-icon>
            </button>
          </div>
        </div>
      </template>
      <b-form @submit.prevent="onSearch(filterName)">
        <div class="filter">
          <div class="filter-item">
            <base-input
              name="username"
              type="text"
              :label="getDictionary('username', 'user')"
              v-model="filter.byAttribute.username"
              placeholder="Inserisci un nome utente"
            />
          </div>
          <div class="filter-item">
            <base-input
              name="email"
              type="text"
              :label="getDictionary('email', 'user')"
              v-model="filter.byAttribute.email"
              placeholder="Inserisci un'email"
            />
          </div>
          <div class="filter-item">
            <base-select
              name="role"
              :label="getDictionary('role', 'user')"
              v-model="filter.byAttribute.role"
              :options="roleOptions"
              :taggable="false"
              :multiple="false"
              :closeOnSelect="true"
            />
          </div>
        </div>
        <div class="actions-filter-sidebar-wrapper">
          <b-button
            type="submit"
            variant="lisaweb"
            size="sm"
            v-b-toggle:sidebar-1
            >Cerca
          </b-button>
          <b-button
            class="btn--transparent btn-reset"
            type="button"
            size="sm"
            @click="onClearFilter(filterName)"
            >Reset
          </b-button>
        </div>
      </b-form>
    </b-sidebar>

    <div class="mt-2">
      <div>
        <b-overlay center :show="isLoading" rounded="sm">
          <users
            @edit="onEdit"
            :fields="fields"
            :repository="repository"
            :resource="resource"
            :filterName="filterName"
            :filterOn="{
              byRelations: ['byRole'],
            }"
            :filteredItems="visibleUsers"
            @destroy="onDestroy"
            @user_permissions="onUpdateUserPermissions"
            @brokers_permissions="onUpdateBrokersPermissions"
            @lisa_for_apps_permissions="onUpdateLisaForAppsPermissions"
            @reports_permissions="onUpdateReportsPermissions"
            :ref="tableRef"
            :onlyActions="['destroy', 'edit', 'lisa_for_apps', 'reports']"
          ></users>
          <template #overlay>
            <div class="text-center">
              <div class="custom-loader"></div>
              <p class="loading-text">Operazione in corso...</p>
            </div>
          </template>
        </b-overlay>
      </div>
    </div>
  </div>
  <div v-else class="mt-3">
    <b-icon icon="info-circle" scale="1.00"></b-icon>
    Non hai i permessi
  </div>
</template>

<script>
import BaseIcon from "@/components/BaseIcon";
import BaseInput from "@/components/form/BaseInput";
import BaseSelect from "@/components/form/BaseSelect";
import {RepositoryFactory} from "@/repositories/RepositoryFactory";
import CustomFiltersMixin from "@/mixins/CustomFiltersMixin";
import SidebarFilterMixin from "@/mixins/SidebarFilterMixin";
import Users from "@/components/tables/Users";
// import BaseInput from "@/components/form/BaseInput";
import {mapActions} from "vuex";
// import { canModule } from "@/utils/permissions";
import {isAdmin} from "@/utils/permissions";

export default {
  mixins: [CustomFiltersMixin, SidebarFilterMixin],
  data() {
    return {
      isLoading: false,
      repository: "user",
      resource: "users",
      tableRef: "UsersTableRef",
      filterName: "filterUsers",
      context: "profile_users",
      activeFilters: {},
      filter: this.initFilter(),
      // Filtri applicati con "Cerca": l'elenco degli utenti è già tutto in
      // pagina (fetchCountUser), quindi si filtra qui invece che sul server.
      appliedFilter: this.initFilter(),
      user_count: null,
      args: {},
      isLoadingCount: true,
      filteredItems: [],
      fields: [
        {
          key: "username",
          label: this.getDictionary("username", "user"),
          // sortable: true,
        },
        {
          key: "email",
          label: this.getDictionary("email", "user"),
          // sortable: true,
        },
        {
          key: "role",
          label: this.getDictionary("role", "user"),
        },
      ],
    };
  },
  computed: {
    countActiveFilters() {
      return Object.keys(this.activeFilters).length;
    },
    computedFilterTitle() {
      return `Filtri${
        this.countActiveFilters ? ` (${this.countActiveFilters})` : ""
      }`;
    },
    roleName() {
      return (user) => user.roles?.find((role) => role)?.name || "";
    },
    roleOptions() {
      return [...new Set(this.filteredItems.map(this.roleName))]
        .filter(Boolean)
        .sort()
        .map((name) => ({ value: name, text: name }));
    },
    visibleUsers() {
      const { username, email, role } = this.appliedFilter.byAttribute;
      const has = (value, text) =>
        !text || (value || "").toLowerCase().includes(text.toLowerCase());
      return this.filteredItems.filter(
        (user) =>
          has(user.username, username) &&
          has(user.email, email) &&
          (!role || this.roleName(user) === role),
      );
    },
  },
  components: {
    Users,
    BaseIcon,
    BaseInput,
    BaseSelect,
  },
  mounted() {
    // Un filtro salvato in precedenza vale anche per l'elenco in pagina.
    this.appliedFilter = JSON.parse(JSON.stringify(this.filter));
    this.fetchCountUser();
  },
  methods: {
    // canModule,
    isAdmin,
    initFilter() {
      let init = {
        byAttribute: {
          email: null,
          username: null,
          role: null,
        },
      };
      return init;
    },
    fetchCountUser() {
      this.isLoading = true;
      this.isLoadingCount = true;
      const Repo = RepositoryFactory.get(this.repository);
      Repo.index("perPage=0&byRole")
        .then((response) => {
          this.filteredItems = response.data.data.filter((object) => {
            return (
              object.id === this.$store.state.auth.user.id ||
              (object.username.indexOf("operatore") < 0 &&
                object.username.indexOf("ws@") < 0 &&
                object.username.indexOf("admin.") < 0 &&
                !object.username.endsWith(".api") &&
                object.username !== "admin" &&
                object.username !== "lisalex")
            );
          });
          // this.user_count = this.filteredItems.filter(
          //   (user) => !this.isAdmin(user, ["servizio"])
          // ).length;
          this.user_count = this.filteredItems.length;
          this.isLoadingCount = false;
        })
        .catch((error) => {
          let errMsg = this.$getErrorMessage(error);
          this.$showSnackbar({
            preset: "error",
            text: `${errMsg}`,
          });
          this.isLoadingCount = false;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    customFilterName(key, prefix = "byAttribute") {
      return `${prefix}.${key}`;
    },
    onEdit(id) {
      this.$router.push({
        name: `${this.resource}.edit`,
        params: {id: `${id}`},
      });
    },
    onAdd() {
      console.debug(
        `license: ${this.$store.state.auth.license}, count: ${this.user_count}`
      );
      if (this.$store.state.auth.license <= this.user_count) {
        this.$showSnackbar({
          preset: "error",
          text: `Limite Utenti Raggiunto`,
        });
      } else {
        this.$router.push({name: `${this.resource}.create`});
      }
    },
    onUpdateUserPermissions(user) {
      this.$router.push({
        name: `${this.resource}.edit2`,
        params: {user: user},
      });
    },
    onUpdateLisaForAppsPermissions(user) {
      this.$router.push({
        name: `lisa_for_apps.index`,
        params: {user: user},
      });
    },
    onUpdateReportsPermissions(user) {
      this.$router.push({
        name: `reports.edit`,
        params: {user: user},
      });
    },
    onUpdateBrokersPermissions(user) {
      this.$router.push({
        name: `brokers.edit3`,
        params: {user: user},
      });
    },
    // isAdmin(user) {
    // let adminRoles = [
    //   "admin-agenzia",
    //   "super-admin",
    //   "admin-assistenza",
    //   "servizio",
    // ];
    //   return user.roles.find((role) => adminRoles.includes(role.name))
    //     ? true
    //     : false;
    // },
    onDestroy(id) {
      const Repo = RepositoryFactory.get(this.repository);
      Repo.destroy(id)
        .then(() => {
          this.$refs[this.tableRef].fetch();
          this.$showSnackbar({
            preset: "success",
            text: "Utente Eliminato con successo",
          });
          this.fetchCountUser();
        })
        .catch((error) => {
          let errMsg = this.$getErrorMessage(error);
          this.$showSnackbar({
            preset: "error",
            text: `${errMsg}`,
          });
        });
    },
    onSearch(name) {
      this.activeFilters = {};
      this.activeFilters = this.retriveActiveFilters();
      this.appliedFilter = JSON.parse(JSON.stringify(this.filter));
      let criteria = this.filter;
      this.saveFilterByName({name, criteria});
    },
    onClearFilter(name) {
      this.resetFilter();
      this.appliedFilter = this.initFilter();
      this.activeFilters = {};
      // this.resetPagination();
      this.removeFilterByName(name);
      // this.removePaginationByName(name);
      this.removeSortByName(name);
      this.$refs[this.tableRef].sortBy = "";
      this.$refs[this.tableRef].sortDesc = false;
    },
    ...mapActions("filters", {
      saveFilterByName: "saveByName",
      removeFilterByName: "removeByName",
    }),
    // ...mapActions("pagination", { removePaginationByName: "removeByName" }),
    ...mapActions("sorts", {
      removeSortByName: "removeByName",
    }),
    onSaveFilter(name, criteria) {
      this.saveFilterByName({name, criteria});
    },
  },
};
</script>
